import { AppChannel, Channel } from "@/config/channel"
import { RecaptchaPlatform } from "@/hooks/use-recaptcha"
import {
  computeErrorType,
  computeHasOther,
  computePostLoginAction,
} from "@/lib/post-login-actions"
import { safeWindow } from "@/lib/safe-window"
import { seapServiceControl } from "@/lib/seap"
import useGlobalParamsStore from "@/store/global-params-store"
import { Err, isOk, Ok, Result } from "@pacote/result"
import useSWRMutation from "swr/mutation"
import { v4 as uuidV4 } from "uuid"
import { z } from "zod"
import {
  ApiFailure,
  baseApiErrorResponseSchema,
  baseApiResponseSchema,
  UnhandledApiError,
} from "./api-config"
import { baseClientFetcher } from "./base-fetchers"
import { FetcherErrorResponse } from "./create-fetcher"
import { InfoMessageList, messagesFetcher } from "./get-messages"

export type BaseLoginPayload = {
  username: string
  password: string
  channel: Channel
  recaptchaToken: string
}

type SsoLoginPayload = BaseLoginPayload & {
  pvd?: string
  platform: RecaptchaPlatform
}

export const VerificationPhoneNumberStateEnum = {
  VALIDATE_PHONE_NUMBER_OK: 0,
  VALIDATE_PHONE_NUMBER_15_DAYS: 1,
  VALIDATE_PHONE_NUMBER_31_DAYS: 2,
} as const

export const VerificationEmailStateEnum = {
  VALIDATE_EMAIL_OK: 0,
  VALIDATE_EMAIL_15_DAYS: 1,
  VALIDATE_EMAIL_31_DAYS: 2,
} as const

export const contactsToVerifySchema = z.object({
  verificationEmailState: z.enum(VerificationEmailStateEnum).optional(),
  verificationPhoneNumberState: z
    .enum(VerificationPhoneNumberStateEnum)
    .optional(),
  contacts: z
    .object({
      name: z.string().optional(),
      surname: z.string().optional(),
      prefix: z.string().optional(),
      phoneNumber: z.string().optional(),
      email: z.string().optional(),
      phoneLockDays: z.number().optional(),
      emailLockDays: z.number().optional(),
      updateContactToken: z.string().optional(), // lo usiamo per la certifica - modifica contatti post login
    })
    .optional(),
})

export type ContactsToVerify = z.infer<typeof contactsToVerifySchema>

export const ContractSettings = {
  NotAccepted: 0,
  Accepted: 1,
} as const

export const SecuritySetting = {
  NoSettingActivated: 0,
  OnlySecurityQuestions: 1,
  OnlyAutoexclusion: 2,
  SecurityQuestionsAndAutoexclusion: 3,
  OnlyAutolimitations: 4,
  SecurityQuestionsAndAutolimitations: 5,
  AutoexclusionAndAutolimitations: 6,
  AllSettingsActivated: 7,
} as const

const loginBlockedTypeSchema = z.union([
  z.literal("BLOCK"),
  z.literal("EXPIRE"),
  z.literal("DISABLE"),
  z.literal("SUSPEND"),
  z.literal("DOC"),
  z.literal("FOREIGN"),
  z.literal("QUESTION"),
  z.literal("BLACK_LIST"),
])

export type LoginBlockedType = z.infer<typeof loginBlockedTypeSchema>

export const ssoLoginResponseSchema = baseApiResponseSchema.extend({
  jwt: z.string().optional(),
  expire: z.string().optional(),
  tokenTouch: z.string().optional(),
  billCodeId: z.number().optional(),
  passwordExpirationDays: z.number(),
  passwordRequired: z.boolean().optional(),
  isLoginBlocked: z.boolean(),
  isSecurityQuestionRequired: z.boolean(),
  isBillNotProtocolled: z.boolean(),
  flagAdvPlus: z.boolean().optional(),
  loginBlockedType: loginBlockedTypeSchema.optional(),
  loginBlockedMessage: z.string().optional(),
  emailAddress: z.string().optional(),
  prefix: z.string().optional(),
  phoneNumber: z.string().optional(),
  fiscalCode: z.string().optional(),
  contactsToVerify: contactsToVerifySchema.optional(),
  voucherStatus: z.string().optional(),
  remainingVoucherDays: z.number().optional(),
  contractSettings: z.enum(ContractSettings).optional(),
  securitySettings: z.enum(SecuritySetting).optional(),
  email: z.string().optional(),
})

export type SsoLoginResponse = z.infer<typeof ssoLoginResponseSchema>

export type MultiAction = {
  type:
    | "ACCOUNT_BLOCKED"
    | "QUESTIONARIO"
    | "VERIFY_CONTACTS"
    | "PASSWORD_EXPIRING"
    | "URL_REDIRECT"
    | "NOTHING"
    | "MUST_ACCEPT_NEW_CONTRACT_AND_SELF_LIMITATIONS"
    | "MUST_ACCEPT_SELF_LIMITATIONS"
    | "MUST_ACCEPT_NEW_CONTRACT_FOR_PLATFORM_MIGRATION"
    | "WELCOME"
    | "MFA_REQUIRED"
  showListModals?: boolean
}

export type ComputedLoginFields = {
  postLoginAction: MultiAction
  hasOther: boolean
  messages: InfoMessageList
}

export type TSsoLoginResponse = SsoLoginResponse & ComputedLoginFields

export type TloginErrorResponse = {
  type:
    | "SOGEI"
    | "UNKNOWN"
    | "CREDENTIALS"
    | "ERROR_RECAPTCHA"
    | "NO-AUTO-LIMITATION-SET"
    | "CONTRACT-NOT-ACCEPTED"
  counter: number
  errorResponse: LoginErrorResponse
}

export const loginErrorResponseSchema = z.object({
  ...baseApiResponseSchema.shape,
  ...baseApiErrorResponseSchema.shape,
  passwordExpirationDays: z.number().optional(),
  isLoginBlocked: z.boolean().optional(),
  isSecurityQuestionRequired: z.boolean().optional(),
  isBillNotProtocolled: z.boolean().optional(),
  flagAdvPlus: z.boolean().optional(),
})

export type LoginErrorResponse = z.infer<typeof loginErrorResponseSchema>

type SsoLoginResult = Result<TSsoLoginResponse, TloginErrorResponse>

/* -------------------------------------------------------------------------------------------------
 * config
 * -----------------------------------------------------------------------------------------------*/

export const ssoLoginV1Endpoint = "/authentication-ms/v1/sso/login"

/* -------------------------------------------------------------------------------------------------
 * fetcher
 * -----------------------------------------------------------------------------------------------*/

const ssoLoginFetcher = async (
  key: string,
  { arg }: { arg: SsoLoginPayload },
): Promise<SsoLoginResult> => {
  const sessionId = uuidV4()

  const loginServiceResult = await baseClientFetcher<
    SsoLoginResponse,
    LoginErrorResponse
  >(
    {
      url: key,
      method: "POST",
      headers: {
        "X-Session-Id": sessionId,
        ...(safeWindow()?.APP_VENDOR === "sisal"
          ? { "x-set-queue": "true" }
          : {}),
      },
      body: {
        username: arg.username,
        password: arg.password,
        recaptchaToken: arg.recaptchaToken,
        platform: arg.platform,
      },
      successSchemaValidator: ssoLoginResponseSchema,
      errorSchemaValidator: loginErrorResponseSchema,
      throwOnValidationError: true,
    },
    { enrichRequestData: false },
  )

  return await handleLoginResult(loginServiceResult, arg.channel, sessionId)
}

export async function handleLoginResult<T extends SsoLoginResponse>(
  loginServiceResult: Result<T, FetcherErrorResponse<LoginErrorResponse>>,
  channel: Channel,
  sessionId: string,
): Promise<SsoLoginResult> {
  if (isOk(loginServiceResult)) {
    return await handleLoginSuccess<T>(loginServiceResult, channel, sessionId)
  } else {
    return handleLoginError(loginServiceResult)
  }
}

async function handleLoginSuccess<T extends SsoLoginResponse>(
  loginServiceResult: Ok<T>,
  channel: Channel,
  sessionId: string,
) {
  sessionStorage.setItem("session-id", sessionId)

  const loginResponse = loginServiceResult.value

  const { jwt, status, billCodeId } = loginResponse

  let messages: InfoMessageList = []

  if (jwt) {
    useGlobalParamsStore.getState().setJwt(jwt)

    if (channel !== AppChannel.ClientDesktopSisalPoker) {
      const messagesServiceResult = await messagesFetcher()

      if (isOk(messagesServiceResult)) {
        messages = messagesServiceResult.value.infoMessageList
      }
    }
  }

  if (
    status === "SUCCESS" &&
    channel === AppChannel.PortaleWebFullResponsiveWwwSisalIt &&
    billCodeId != null &&
    window.seapweb != null
  ) {
    await seapServiceControl(billCodeId)
  }

  const postLoginAction = computePostLoginAction(
    loginResponse,
    channel,
    messages,
  )

  const hasOther = computeHasOther(loginResponse, messages)

  return Ok({
    ...loginResponse,
    postLoginAction,
    hasOther,
    messages,
  })
}

function handleLoginError(
  loginServiceResult: Err<FetcherErrorResponse<LoginErrorResponse>>,
): Result<never, TloginErrorResponse> {
  if (loginServiceResult.value.status !== 422) {
    throw new UnhandledApiError({
      status: ApiFailure.UNPROCESSABLE_ERROR,
      message: `A non 422 httpStatus was returned on request: ${ssoLoginV1Endpoint}`,
    })
  }

  const errorResponse = loginServiceResult.value.data

  const errorType = computeErrorType(loginServiceResult.value)

  const counter = errorResponse.error?.errorInfos?.counter

  return Err({
    type: errorType,
    counter: 4,
    errorResponse,
    ...(Array.isArray(counter) && { counter: Number(counter[0]) + 1 }),
  })
}

/* -------------------------------------------------------------------------------------------------
 * hooks
 * -----------------------------------------------------------------------------------------------*/

export const useSsoLoginV1Mutation = () =>
  useSWRMutation(ssoLoginV1Endpoint, ssoLoginFetcher, {
    populateCache: true,
  })
